/* eslint-disable no-unused-vars */
import { createContext, useContext, useState, useCallback, ReactNode, useMemo } from 'react';
import { CircularProgress } from '@mui/material';

import AgModal, { type AgModalProps } from '../../stories/AgModal';
import AgButton, { type AgButtonProps } from '../../stories/Components/AgButton';

type ModalContentProps = Omit<AgModalProps, 'open' | 'setOpen' | 'onCloseModal'> & {
  actions?: AgButtonProps[];
  autoClose?: boolean;
  onCloseCallback?: () => void;
};

type ModalProps = {
  isOpen?: boolean;
  modalContent: ModalContentProps;
  openModal: (props: ModalContentProps) => void;
  closeModal: () => void;
};

const defaultModalContext: ModalProps = {
  isOpen: false,
  modalContent: {},
  openModal: () => {},
  closeModal: () => {},
};

const ModalContext = createContext<ModalProps>(defaultModalContext);

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalContent, setModalContent] = useState<ModalContentProps>({});
  const [loadingIds, setLoadingIds] = useState<number[]>([]);

  const openModal = useCallback((props: ModalContentProps) => {
    setModalContent({ ...props });
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setModalContent({});
    modalContent.onCloseCallback?.();
  }, [modalContent]);

  const handleActionClick = useCallback(async (callback: Function, index: number) => {
    setLoadingIds((prev) => [...prev, index]);
    callback && await callback();
    setLoadingIds((prev) => prev.filter((item) => item !== index));
  }, []);

  const asyncActionButtons = useMemo(() => (modalContent?.actions || []).map(
    ({ onClick, ...otherProps }, index) => {
      const isLoading = loadingIds.includes(index);
      return (
        <AgButton
          key={index}
          loading={isLoading}
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress color='inherit' size={13} /> : null}
          onClick={() => (onClick ? !isLoading && handleActionClick(onClick, index) : closeModal())}
          {...otherProps}  
        />
      );
    }
  ), [closeModal, handleActionClick, loadingIds, modalContent.actions]);

  return (
    <ModalContext.Provider value={{ isOpen, openModal, closeModal, modalContent }}>
      {children}
      {isOpen && (
        <AgModal 
          open={isOpen} 
          setOpen={(modalContent?.autoClose ?? true ) ? closeModal : () => null} 
          onCloseModal={closeModal}
          {...modalContent}
          footer={modalContent.footer || asyncActionButtons.length ? asyncActionButtons : null}
        />
      )}
    </ModalContext.Provider>
  );
};

export const useModal = (): ModalProps => useContext(ModalContext);
